import { localeFormatDefault } from '@/plugins/date-print';
import { isSameDay, format } from 'date-fns';
import { dateFnsFormat } from '@/helpers/dateFns';

import { rServices, services } from '@/plugins/api';
import appConfig from '@/config';
import { base64ToBlob } from '@/utils/base64-to-blob';
import { getUniqueValuesArray } from '@/helpers/array';
import { EVENT_USER_STATUS, EVENT_CHATS_TYPES, DEFAULT_NOMINATION, EVENT_SPECIAL_PARAMS_FOR_UPDATE, EVENT_SPECIAL_PARAMS_FOR_DATES } from '@/modules/Events/constants.js';
import { TEAM_TYPES } from '@/modules/Team/constants';
import { PER_PAGE } from '@/constants/pagination';
import { indexedArray } from '@/utils/indexedArray';

import BaseModel from './base';
import EventOptions from './event-options';
import Nomination from './nomination';
import Stage from './stage';
import Chats from '@/models/list/chats.js';
import Participant from '@/models/list/participant';
import Team from '@/models/list/team';
import EventCertificate from '@/models/list/event-certificate';
import User from '@/utils/user';
import EventAttribute from './event-attribute';
import EventDocument from './event-document';
import NominationOptions from './nomination-options';
import EventForm from './event-form';
import EventPage from './event-page';
import { apiService } from '@/services/APIService';
import { rHandler } from '@/plugins/RHandler';
import { DEFAULT_PARAMS } from '@/utils/objectsRHandler';
import EventActivity from './eventActivity/index';
import AwardSettings from './award-settings/index';
import OrganizerProfile from './organizer-profile';
import { smartNumberSort } from '@/modules/UIDesign/functions';
import { getEventAreaIds, validateEventArea } from '@/models/event-area';

export default class Event extends BaseModel {
  #user;

  constructor(event, url) {
    super(event, url);

    this.short_name = event.short_name;
    this.members = event.members || [];
    this.teams = [];
    this.active_members_count = event.active_members_count;
    this.all_event_members_count = event.all_event_members_count;
    this.assigned_organizers_count = event.assigned_organizers_count;
    this.members_count = event.members_count;
    this.request_members_count = event.request_members_count;
    this.reject_members_count = event.reject_members_count;
    this.restricted_members_count =  event.restricted_members_count;
    this.status = event.status;
    this.start_at = event.start_at && localeFormatDefault(event.start_at);
    this.end_at = event.end_at && localeFormatDefault(event.end_at);
    this.announcement = event.announcement;
    this.target = event.target;
    this.category_id = event.category_id;
    this.stages_count = event.stages_count;
    this.images = event.images || [];
    this.registerLink = `${window.location.origin}/event/register/${event.id}`;
    this.options = event.options ? new EventOptions(event.options, 'event-options') : null;
    this.options_id = event.options_id;
    this.area = event.area || null;
    this.area_id = event.area_id;
    this.owner_id = event.owner_id;
    this.organization = new OrganizerProfile(event.organization || {});
    this.organization_name = event.organization_name;
    this.main_image = event.main_image;
    this.experts = event.experts || [];
    this.highest_event = event.highest_event;
    this.nominations = event.nominations ? smartNumberSort((event.nominations || []).map((nomination) => new Nomination(nomination)), 'name') : [];
    this.nomination_options = event.nomination_options ? (event.nomination_options || []).map((item) => new NominationOptions(item)) : [];
    this.stages = event.stages ? (event.stages || []).map((stage) => new Stage(stage)) : [];
    this.templates = event.templates || [];
    this.template_id = event.template_id ?? null;
    this.presentation_url = event.presentation_url;
    this.target = event.target;
    this.request_status = event.request_status || null;

    this.available_polls_count = event.available_polls_count || null;
    this.dont_complete_polls_count = event.dont_complete_polls_count || null;
    this.places_count = event.places_count || null;
    this.unread_notifications_count = event.unread_notifications_count || 0;
    this.news_count = event.news_count || 0;
    this.event_user_requests = event.event_user_requests || []; // { nomination_id, status }[]

    this.main_address = event.main_address;

    this.event_stage_start_at = event.start_at && localeFormatDefault(event.start_at); // old: event_stage_start_at
    this.event_stage_end_at = event.end_at && localeFormatDefault(event.end_at); // old: event_stage_end_at

    this.event_stage_range_at = [
      ...this.event_stage_start_at ? [format(dateFnsFormat.parseDate(this.event_stage_start_at), 'dd.MM.yyyy')] : [],
      ...(this.event_stage_start_at && this.event_stage_end_at && !isSameDay(dateFnsFormat.parseDate(this.event_stage_start_at), dateFnsFormat.parseDate(this.event_stage_end_at))) ? [format(dateFnsFormat.parseDate(this.event_stage_end_at), 'dd.MM.yyyy')] : [],
    ];

    this.event_range_at = [
      ...this.start_at ? [format(dateFnsFormat.parseDate(this.start_at), 'dd.MM.yyyy')] : [],
      ...(this.start_at && this.end_at && !isSameDay(dateFnsFormat.parseDate(this.start_at), dateFnsFormat.parseDate(this.end_at))) ? [format(dateFnsFormat.parseDate(this.end_at), 'dd.MM.yyyy')] : [],
    ];

    this.templates_dates = event.templates_dates || {};
    this.templates_start_at = this.templates_dates.start_at && localeFormatDefault(this.templates_dates.start_at);
    this.templates_end_at = this.templates_dates.end_at && localeFormatDefault(this.templates_dates.end_at);

    this.templates_range_at = [
      ...this.templates_start_at ? [format(dateFnsFormat.parseDate(this.templates_start_at), 'dd.MM.yyyy')] : [],
      ...(this.templates_start_at && this.templates_end_at && !isSameDay(dateFnsFormat.parseDate(this.templates_start_at), dateFnsFormat.parseDate(this.templates_end_at))) ? [format(dateFnsFormat.parseDate(this.templates_end_at), 'dd.MM.yyyy')] : [],
    ];

    this.template_options = new EventOptions(event.template_options || {}, 'event-template-options');

    // organizers
    this.step = null;
    this.address_id = event.address_id;

    this.level = {
      id: event.level,
      name: appConfig.EVENT_LEVELS_TEXT[event.level],
    };
    this.document = null;

    this.is_favorite = Boolean(event.is_favorite);
    this.format = event.format;

    // chats (telegram)

    this.chats = new Chats('event', event.id, event.telegram || []);

    this.results = null;
    this.eventCertificates = [];

    this.qr_link = event.qr_link || '';

    this.associations = event.associations;

    // organizers
    this.organizers = [];

    this.is_user_event_member = false;

    this.subordinated = event.subordinated || false;

    this.parent_event_id = event.parent_event_id || null;

    this.is_subordinated = event.is_subordinated ?? false;

    this.award_settings = null;

    this.levels_count = event.levels_count;

    this.change_status_at = event.change_status_at && localeFormatDefault(event.change_status_at);

    this.moderation_tags = event.moderation_tags || [];
    [this.moderation_tag] = this.moderation_tags;

    this.#user = new User();

    this.isLoadingNominations = false;
  }

  update(rParams = DEFAULT_PARAMS) {
    const { data, obs } = rParams;
    const getSpecialRoute = (keys) => {
      if (keys.some((i) => EVENT_SPECIAL_PARAMS_FOR_UPDATE.includes(i))) {
        return '/edit';
      }
      if (keys.some((i) => EVENT_SPECIAL_PARAMS_FOR_DATES.includes(i))) {
        return '/dates';
      }
      return '';
    };
    return rHandler(
      apiService.put(`${this.url}/${this.id}${getSpecialRoute(Object.keys(data))}`, data),
      (response) => {
        Object.assign(this, data);
        return { response };
      },
      (error) => ({ error }),
      obs,
    );
  }

  get shortName() {
    if ((this.short_name || '').length <= (this.name || '').length) {
      return this.short_name || '';
    }
    return this.name || '';
  }

  get fullName() {
    if ((this.short_name || '').length <= (this.name || '').length) {
      return this.name || '';
    }
    return this.short_name || '';
  }

  get isClosed() {
    return [appConfig.EVENT_STATUS.CLOSED, appConfig.EVENT_STATUS.MODERATE_REFUSE].includes(this.status);
  }

  get isDraft() {
    return this.status === appConfig.EVENT_STATUS.DRAFT;
  }

  get isModerate() {
    return this.status === appConfig.EVENT_STATUS.MODERATE;
  }

  get isActive() {
    return this.status === appConfig.EVENT_STATUS.ACTIVE;
  }

  get isSubordinatedTemplate() {
    return this.status === appConfig.EVENT_STATUS_CONCEALED.TEMPLATE_SUBORDINATED;
  }

  get isAdminTemplate() {
    return this.status === appConfig.EVENT_STATUS_CONCEALED.TEMPLATE;
  }

  get isSingle() {
    // Индивидуальное участие
    return this.options?.event_member_type === appConfig.EVENT_MEMBER_TYPE.SINGLE;
  }

  get isTeamEvent() {
    // Командное участие
    return this.options?.event_member_type === appConfig.EVENT_MEMBER_TYPE.TEAM;
  }

  get isTeamMixed() {
    // Смешанное участие
    return this.options?.event_member_type === appConfig.EVENT_MEMBER_TYPE.SINGLE_TEAM;
  }

  get isOlympics() {
    return this.options?.is_olympics;
  }

  get isLowest() {
    // Самое нижестоящее мероприятие
    return !this.options?.can_subordinate;
  }

  get isMyOrganization() {
    // Является ли организация этого мероприятия моей?
    return this.organization?.id === this.#user?.profile?.id;
  }

  get isMyHighest() {
    const { id, highest_event } = this;
    return highest_event?.id === id;
  }

  get isHighest() {
    const { isMyHighest, options } = this;
    return isMyHighest && options?.can_subordinate;
  }

  get subordinatedLevels() {
    const { templates, level } = this;
    const subordinatedLevels = Object.values(appConfig.EVENT_LEVELS).filter((levelType) => levelType > level.id);
    return templates
      .filter(({ level: templateLevel }) => subordinatedLevels.includes(templateLevel))
      .sort((a, b) => a.level - b.level);
  }

  get subordinatedLowerLevel() {
    const [lower] = this.subordinatedLevels;
    return lower;
  }

  get defaultNomination() {
    const defaultNomination = this.nominations.find((nomination) => nomination.name === DEFAULT_NOMINATION.NAME);
    return defaultNomination || null;
  }

  get nominationsWithoutDefault() {
    const { defaultNomination } = this;
    return this.nominations.filter((nomination) => nomination.id !== defaultNomination.id);
  }

  get actualNominations() {
    const { defaultNomination, nominationsWithoutDefault } = this;
    if (!defaultNomination) {
      return this.nominations || [];
    }

    // БАГ - has_nominations = false, хотя номинации есть
    if (this.options?.has_nominations) {
      return nominationsWithoutDefault;
    }

    return [defaultNomination];
  }

  get memberNominations() {
    const { nominations, event_user_requests } = this;
    return nominations.filter((nomination) => !!event_user_requests.find((request) => request.nomination_id === nomination.id));
  }

  get isWithoutStages() {
    return this.options?.is_without_stages;
  }

  get isWithFastRegister() {
    return this.options?.is_without_moderation;
  }

  get showQR() {
    const { qr_link } = this;
    return qr_link && this.isWithFastRegister;
  }

  get isLocal() {
    return this.level.id === appConfig.EVENT_LEVELS.local;
  }

  get isMunicipal() {
    return this.level.id === appConfig.EVENT_LEVELS.municipal;
  }

  get isRegional() {
    return this.level.id === appConfig.EVENT_LEVELS.regional;
  }

  get isFederal() {
    return this.level.id === appConfig.EVENT_LEVELS.federal;
  }

  get isUserPassingByAge() {
    const { options } = this;
    return this.#user.profile.age >= options.min_age && this.#user.profile.age <= options.max_age;
  }

  get applicationsProgress() {
    const { active_members_count, options } = this;
    const maxProgressProcends = 100;
    const maxEventParticipantsCount = options.max_participants || active_members_count || 1;
    return Math.ceil((maxProgressProcends * active_members_count) / maxEventParticipantsCount);
  }

  async fetchImages() {
    const params = {
      'search[event_id]': this.id,
      sort: 'id',
    };
    const { response, error } = await rServices.events.getImages({ params });
    if (!error) this.images = response.data.data;
  }

  async fetchNominations(data, cached = false) {
    this.isLoadingNominations = true;
    const params = {
      ...data,
      'search[event_id]': this.id,
      'per-page': PER_PAGE.infinity,
    };

    const method = [
      {
        [true]: 'getEventNominations',
        [false]: 'getPublicNominations',
      }[this.#user?.isAuthenticated],
      ...cached && (typeof cached === 'boolean') ? ['Cached'] : [],
    ].join('');

    const { response, error } = await rServices.events?.[method]?.({ params });
    if (error) {
      return error;
    }

    const nominations = smartNumberSort((response?.data?.data || []).map((nomination) => new Nomination(nomination)), 'name');
    this.nominations = nominations;
    this.isLoadingNominations = false;
    return response;
  }

  async fetchAwardSettings(params = {}) {
    const payload = {
      args: [this.id],
      params,
    };
    const { response, error } = await rServices.events.getAwardSettings(payload);
    const responseItem = response?.data?.data || null;
    const item = (error || !responseItem) ? null : new AwardSettings(responseItem, `events/${this.id}/award-settings`);
    if (!error) {
      this.award_settings = item;
    }

    return { item, response, error };
  }

  async fetchOptions(params = null) {
    const args = [this.options.id];

    const { response, error } = await rServices.events.showEventOptions({ args, params });
    if (error) {
      return;
    }

    this.options = new EventOptions(response.data.data, 'event-options');
  }

  async updateOptions(params) {
    const id = this.options?.id || this.options_id || null;
    if (!id) return null;
    const response = await rServices.events.updateEventOptions({ args: [id, params] });
    if (!response.error) {
      const { data } = response.response.data;
      this.options = new EventOptions(data, 'event-options');
    }
    return response;
  }

  async updateEventArea(params) {
    const id = this.area?.id || this.area_id || null;
    if (!id) return null;
    const data = {
      ...params,
      ...getEventAreaIds(params),
      event_id: this.id,
      id,
    };
    const validation = await validateEventArea(this.level, data);
    if (validation?.error) return validation;
    const result = await rServices.events.updateEventArea({ args: [id], data });
    if (!result.error && result.response?.data?.data) {
      this.area = result.response.data.data;
    }
    return result;
  }

  async openDocument() {
    if (!this.document) {
      const response = await services.events.getPdf(this.id, { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      this.document = base64ToBlob(response.data);
    }
    window.open(URL.createObjectURL(this.document), '_blank');
  }

  close(payload = {}) {
    return rServices.events.closeEvent({ args: [this.id], data: payload });
  }

  async publish() {
    const { response, error } = await rServices.events.publishEvent({ args: [this.id] });
    if (!error) {
      this.status = this.isLocal ? appConfig.EVENT_STATUS.ACTIVE : appConfig.EVENT_STATUS.MODERATE;
    }
    return { response, error };
  }

  clone() {
    return rServices.events.cloneEvent({ args: [this.id] });
  }

  async deleteImage(id) {
    const response = await rServices.events.deleteImage({ args: [id] });
    this.images = this.images.filter((i) => i.id !== id);
    await this.#checkMainImage();
    return response;
  }

  async addImage(imageName, isMain) {
    const params = {
      event_id: this.id,
      name: imageName,
      is_main: isMain,
    };
    const { response, error } = await rServices.events.createImage({ params });
    if (!error) this.images.push(response.data.data);
    await this.#checkMainImage();
    return response;
  }

  async makeMainImage(id) {
    const params = {
      is_main: true,
    };
    const { error } = await rServices.events.updateImage({ args: [id], params });
    if (!error) {
      const mainImage = this.images.find((i) => i.id === id);
      this.main_image = mainImage;
      this.images = [{ ...mainImage, is_main: true }, ...this.images.filter((i) => i.id !== id).map((i) => ({ ...i, is_main: false }))];
    }
  }

  async #checkMainImage() {
    const existsMainImage = this.images.find((image) => image.is_main);
    const [firstImage] = this.images;
    if (!this.main_image && existsMainImage?.id) {
      this.main_image = existsMainImage;
      return;
    }
    if (!existsMainImage && firstImage) {
      await this.makeMainImage(firstImage.id);
      return;
    }
    if (existsMainImage?.id !== this.main_image?.id) this.main_image = null;
  }

  // organizers steps
  async fetchStep() {
    const { response, error } = await rServices.events.getOrganizersStep({ params: { 'search[event_id]': this.id } });
    if (!error) [this.step] = response.data.data;
  }

  async setStep(step) {
    const { response, error } = await rServices.events.addOrganizersStep({ args: [this.step.id], data: { step } });
    if (!error) this.step = response.data.data;
  }

  nextStep() {
    return this.setStep(this.step.step + 1);
  }

  backStep() {
    return this.setStep(this.step.step - 1);
  }

  get isOwner() {
    return this.#user?.isAuthenticated && this.owner_id === this.#user?.data?.id;
  }

  // EVENT CHATS (telegram)

  get membersChat() {
    return this.chats.list.find((chat) => chat.options.type === EVENT_CHATS_TYPES.members) || null;
  }

  get expertsChat() {
    return this.chats.list.find((chat) => chat.options.type === EVENT_CHATS_TYPES.experts) || null;
  }

  get organizatorsChat() {
    return this.chats.list.find((chat) => chat.options.type === EVENT_CHATS_TYPES.organizators) || null;
  }

  async userInfoCompleted() {
    // return rServices.events.checkUserInfoCompleted({ args: [this.id] });
    const { item, ...other } = await this.getFormMember(this.#user?.data?.id);
    return { item: this.getUserInfoFromForm(item), ...other };
  }

  getUserInfoFromForm(item) {
    const nominationsIds = this.memberNominations.map(({ id }) => id);
    const originAttributes = item.attributes.filter(({ nomination_id }) => nominationsIds.includes(nomination_id));
    const originDocuments = item.documents.filter(({ nomination_id }) => nominationsIds.includes(nomination_id));
    const allGroupedAttributes = indexedArray(originAttributes, 'nomination_id', { type: 'group' });
    const allGroupedDocuments = indexedArray(originDocuments, 'nomination_id', { type: 'group' });

    const attributes = (originAttributes || []).filter((attribute) => attribute && !attribute.isSystem && !attribute.user_value);
    const documents = (originDocuments || []).filter((document) => document && !EventDocument.filesFrom(document).length);

    const groupedAttributes = indexedArray(attributes, 'nomination_id', { type: 'group' });
    const groupedDocuments = indexedArray(documents, 'nomination_id', { type: 'group' });

    const byNominations = Object.keys({ ...allGroupedAttributes, ...allGroupedDocuments })
      .reduce((acc, nominationId) => {
        const indexKey = ['null', 'undefined'].includes(nominationId) ? 'default' : nominationId;
        acc[indexKey] = {
          allAttributes: allGroupedAttributes[nominationId] || [],
          allDocuments: allGroupedDocuments[nominationId] || [],
          attributes: groupedAttributes[nominationId] || [],
          documents: groupedDocuments[nominationId] || [],
        };
        return acc;
      }, {});

    const nominationIds = Object.keys(byNominations)
      .map((nominationId) => {
        if (['null', 'undefined', 'default'].includes(nominationId)) return null;
        const normalizedId = Number(nominationId);
        return Number.isNaN(normalizedId) ? nominationId : normalizedId;
      });

    return {
      documents,
      attributes,
      length: Number(attributes.length + documents.length),
      lengthRequired: [...attributes, ...documents].filter(({ required }) => required).length,
      byNominations,
      nominationIds,
      model: item,
    };
  }

  get hasUserInfoRequest() {
    const { userInfoCompleted, isClosed } = this;
    return !!(userInfoCompleted && typeof userInfoCompleted === 'function') && !isClosed;
  }

  // Members

  async getMembers(additionalParams) {
    const { getMemberEvent, getPublicMemberEvent } = rServices.rewards;
    const method = this.isOwner ? getMemberEvent : getPublicMemberEvent;

    const params = {
      'search[member_status]': EVENT_USER_STATUS.STATUS_ACTIVE,
      // 'per-page': PER_PAGE.infinity, // Избавляемся от этого, оно плохо кончится
      ltp: 1,
      ...additionalParams,
    };
    const args = [this.id];

    const { response, error } = await method({ args, params });
    if (error) {
      return { error };
    }

    this.members = response.data.data.map((member) => new Participant(member));

    return { response };
  }

  get activeRequestsMember() {
    const { event_user_requests } = this;
    return (event_user_requests || []).filter(({ status }) => status === EVENT_USER_STATUS.STATUS_ACTIVE);
  }

  get isActiveEventMember() {
    const { activeRequestsMember } = this;
    return !!activeRequestsMember.length;
  }

  get isUserEventMember() {
    return this.is_user_event_member;
  }

  get canCreateManyTournamentRounds() {
    return this.template_options?.is_create_tournament_many_rounds ?? true;
  }

  get canCreatePolls() {
    return this.template_options?.is_created_polls ?? true;
  }

  async checkIsUserEventMember() {
    const params = {
      'search[id]': this.#user.data?.id || null,
    };
    const args = [this.id];

    const { response, error } = await rServices.events.getPublicEventMembers({ args, params });
    if (error || !response.data.data.length) {
      this.is_user_event_member = false;
      return;
    }

    this.is_user_event_member = true;
  }

  // Teams

  async getTeams(additionalParams) {
    const { getMemberEventTeams, getPublicMemberEventTeams } = rServices.rewards;
    const method = this.isOwner ? getMemberEventTeams : getPublicMemberEventTeams;

    const params = {
      expand: 'members',
      // 'per-page': PER_PAGE.infinity,
      ...additionalParams,
    };
    const args = [this.id];

    const { response, error } = await method({ args, params });
    if (error) {
      return { error };
    }

    this.teams = response.data.data.map((team) => new Team(team, TEAM_TYPES.stage));

    return { response };
  }

  get userTeam() {
    return this.teams.find((team) => team.members.some((member) => member.id === this.#user.data.id));
  }

  // Un/assigned Prize Places Count

  // get assignedPrizePlacesForMembersCount() {
  //   return this.members.reduce((acc, member) => {
  //     const { prize_places } = member;
  //     if (!prize_places) {
  //       return acc;
  //     }

  //     return acc + prize_places.length;
  //   }, 0);
  // }

  // get assignedPrizePlacesForTeamsCount() {
  //   return this.teams.reduce((acc, team) => {
  //     const { places } = team;
  //     if (!places) {
  //       return acc;
  //     }

  //     return acc + places.length;
  //   }, 0);
  // }

  // get hasUnassignedPrizePlaces() {
  //   const map = {
  //     [EVENT_MEMBER_TYPE.single]: this.assignedPrizePlacesForMembersCount,
  //     [EVENT_MEMBER_TYPE.team]: this.assignedPrizePlacesForTeamsCount,
  //   };

  //   const assignedPrizePlacesCount = map[this.options.event_member_type];

  // return assignedPrizePlacesCount < this.options.prize_place_count;
  // }

  async getUnassignedPrizePlaces() {
    const { getEventPlaces, getPublicEventPlaces } = rServices.rewards;
    const method = this.isOwner ? getEventPlaces : getPublicEventPlaces;

    const params = {
      'search[empty_place]': 1,
    };
    const args = [this.id];

    const { response } = await method({ args, params });
    return response?.data?.data || [];
  }

  // Results

  async getResults(additionalParams) {
    const userId = this.#user.data.id;

    const params = {
      ...additionalParams,
      'search[id]': this.id,
      expand: 'stages,options,places,nominations,scores',
    };
    const args = [userId];

    const { response, error } = await rServices.events.getEventsResults({ args, params });
    if (error) {
      return { error };
    }

    const getNominationName = (nominations, nominationId) => {
      const nomination = nominations.find(({ id }) => id === nominationId);

      return nomination.name;
    };

    const results = response.data.data.map((result) => {
      const { stages, events } = result.places;

      const stagePlaces = stages.map((stagePlace) => {
        const { name: stageName, score } = result.stages.find(({ id }) => id === stagePlace.id);
        const nominationName = getNominationName(result.nominations, stagePlace.nomination_id);

        return {
          ...stagePlace,
          nominationName,
          stageName,
          score,
        };
      });

      const eventPlaces = events.map((eventPlace) => {
        const eventName = result.short_name;
        const nominationName = getNominationName(result.nominations, eventPlace.nomination_id);

        return {
          ...eventPlace,
          nominationName,
          eventName,
        };
      });

      return {
        ...result,
        places: {
          stages: stagePlaces,
          events: eventPlaces,
        },
      };
    });

    this.results = results;
    response.data.data = results;

    return { response };
  }

  // Event Certificates

  async getEventCertificatesForMember() {
    const params = {
      'search[event_id]': this.id,
      'search[user_id]': this.#user.data.id,
    };

    const { response, error } = await rServices.events.getCertificates({ params });
    if (error) {
      return { error };
    }

    this.eventCertificates = response.data.data.map((eventCertificate) => new EventCertificate(eventCertificate));

    return { response };
  }

  getEventCertificateFile(nominationId) {
    const certificate = this.eventCertificates.find(({ nomination_id }) => nomination_id === nominationId);
    const eventName = this.short_name;

    return certificate.getFile(eventName);
  }

  // Organizers

  async getOrganizers() {
    if (!this.isHighest) {
      const params = {
        'search[event_chain]': this.id,
        expand: 'organization',
      };
      const { response } = await rServices.events.getPublicEvents({ params });
      const organizers = (response?.data?.data || []).map(({ organization }) => organization);
      if (!organizers.length) {
        organizers.push(this.organization);
      }
      this.organizers.push(...getUniqueValuesArray(organizers));
    } else {
      this.organizers.push(this.organization);
    }
  }

  getAttributes(params) { // Все аттрибуты со всех цепочек
    return this.#getRequirements({
      method: rServices.events.getEventGlobalAttributes,
      Model: EventAttribute,
      url: 'event-global-attributes',
      params,
    });
  }

  getDocuments(params) { // Все документы со всех цепочек
    return this.#getRequirements({
      method: rServices.events.getNewEventDirectoryDocuments,
      Model: EventDocument,
      url: 'event-directory-document',
      params,
    });
  }

  getForm(params) {
    return this.#getRequirements({
      method: rServices.events.getEventForm,
      Model: EventForm,
      params,
      multiple: false,
    });
  }

  getFormMember(memberId, params, cached = false) {
    const method = {
      [true]: 'getEventFormMemberCached',
      [false]: 'getEventFormMember',
    }[cached];
    return this.#getRequirements({
      method: rServices.events[method],
      args: [memberId],
      Model: EventForm,
      params,
      multiple: false,
    });
  }

  async getRegistrationRequirements(memberId) {
    const results = await Promise.all([
      this.getAttributes({ 'per-page': PER_PAGE.infinity }),
      this.getDocuments({ 'per-page': PER_PAGE.infinity }),
      this.getFormMember(memberId, {}, false),
    ]);
    results.forEach(({ error, response }) => {
      if (error) throw error;
      if (!response || (response.data?.result_code && response.data.result_code !== 'FLSCS')) {
        throw new Error('Не удалось загрузить требования мероприятия');
      }
    });
    const [attributes, documents, form] = results;
    const withAnswers = (items, answers, isAttribute) => {
      const values = new Map((answers || []).map((item) => [String(item.id), item]));
      items.forEach((item) => {
        const answer = values.get(String(item.id));
        if (!answer) return;
        const value = answer.response?.user_value !== undefined ? answer.response.user_value : answer.user_value;
        if (isAttribute) {
          item.setValue(value);
          if (item.is_many_options && Array.isArray(item.options)) {
            item.user_value = Array.isArray(value) ? [...value] : String(value ?? '')
              .split(String.fromCharCode(30)).flatMap((part) => part.split('␞')).filter(Boolean);
          }
        } else {
          item.setValues(EventDocument.filesFrom(answer));
          if (answer.doc_count != null) item.doc_count = EventDocument.getDocCount(answer);
        }
      });
      return items;
    };
    return {
      attributes: withAnswers(attributes.items, form.item.attributes, true),
      documents: withAnswers(documents.items, form.item.documents, false),
    };
  }

  #getRequirements({ args, params, method, Model, url, multiple = true }) {
    return method({ args: [
      this.id,
      ...(args || []),
      { sort: 'id', 'per-page': PER_PAGE.infinity, ...params },
    ] }).then(({ response, error }) => ({
      response,
      error,
      ...multiple ? {
        items: (response?.data?.data || []).map((item) => new Model(item, url)),
      } : {
        item: new Model(response?.data?.data || {}, url),
      },
    }));
  }

  getPagesConstructor(params) {
    return rServices.eventPages.getPages({ args: [this.id], params }).then(({ response, error }) => ({
      response,
      error,
      items: (response?.data?.data || []).map((item) => new EventPage(item)),
    }));
  }

  savePagesConstructor(data) {
    return rServices.eventPages.savePages({ args: [this.id], data }).then(({ response, error }) => ({
      response,
      error,
      items: (response?.data?.data || []).map((item) => new EventPage(item)),
    }));
  }

  get isManyLevels() {
    return this.organizers.length > 1;
  }

  async switchFavorite() {
    const { id, is_favorite } = this;
    const params = {
      data: {
        event_ids: [id],
      },
    };

    const method = is_favorite ? 'deleteFavoriteEvents' : 'addFavoriteEvents';
    const result = await rServices.events[method](params);
    return result;
  }

  async getAgreementText(ids = []) {
    const { id } = this;
    const params = {
      args: [id],
      data: {
        nomination_ids: ids,
      },
    };

    return rServices.events.getAgreementText(params);
  }

  async getFillingScore(params) {
    const { id } = this;
    const payload = {
      args: [id],
      params: {
        ...params,
      },
    };

    const { response, error } = await rServices.events.getFillingScore(payload);
    if (!error) {
      const item = response?.data?.data || {};
      return {
        item: {
          ...item,
          color: 'ui-warning',
          ...item.score <= 20 ? { color: 'ui-error' } : {},
          ...item.score >= 65 ? { color: 'ui-success' } : {},
        },
        response,
      };
    }
    return {
      error,
    };
  }

  // TODO: масштабировать для регистрации на мероприятие
  get registerTitle() {
    const options = this.options || {};
    const { isMember } = this.#user;
    const { event_user_requests, isActive } = this;
    if (isMember && event_user_requests?.length && isActive) {
      return 'Новая заявка';
    }
    return 'Принять участие';
    // const options = this.options || {};
    // const isRegisterPeriod = options.isRegisterPeriod || this.isHighest;
    // switch (true) {
    //   case this.isClosed:
    //     return 'Мероприятие завершено';
    //   case options.isRegisterNotStart:
    //     return 'Запись еще не началась';
    //   case isRegisterPeriod:
    //     return 'Принять участие';
    //   case options.isRegisterEnd:
    //   default:
    //     return 'Запись завершена';
    // }
  }

  get canRequestByNominationOptions() {
    return !!(this.nomination_options || []).some(({ start_accepting_at, end_accepting_at }) => start_accepting_at && end_accepting_at);
  }

  get hasNominations() {
    return this.options.has_nominations;
  }

  get hasGroups() { // Работает корректно, если запросить `fetchNominations({ expand: 'groups' })`
    return this.nominations.some(({ hasGroupsWithoutBase }) => hasGroupsWithoutBase);
  }

  nominationsName(options) {
    const { frontend_params } = this.options || {};
    return frontend_params?.getEntityName?.('nominations_override', options) || 'номинация';
  }

  getActivities(params) {
    return this.#getRequirements({
      method: this.#user?.isAuthenticated ? rServices.events.getEventActivities : rServices.events.getEventActivitiesPublic,
      Model: EventActivity,
      params: {
        expand: 'images,experts,addresses,videos,assign,documents',
        ...params,
      },
      url: `events/${this.id}/activities`,
    });
  }
}
